/* of-bench-widget.js
   The header workbench cart: icon, count, deal gauge. And, since the
   hybrid rail landed, the header's always-visible progress rail too.

   2026-08-29: this file used to own a separate header slot with its own
   dropdown ladder sitting beside the cart bag. The bench and the cart are
   one icon now (snippets/of-workbench-cart.liquid) and the ladder lives in
   the cart drawer, so what is left here is the icon's state: how many
   items are on the benchtop, and what colour the deal gauge under it runs.

   The rail (snippets/of-bench-rail.liquid) came back on top of that, and
   it is painted from the SAME computation as the gauge, in the same
   publish. There is no second fetch, no second tag resolve and no second
   call into the core: one refresh feeds the icon, the rail and the
   drawer, so no two of them can disagree about a tier.

   Division of labour:
     - of-bench-core.js does every piece of ladder math. Pure, tested.
     - this file fetches the cart, resolves product tags, calls the core,
       paints the gauge and the rail, caches the answer in sessionStorage
       for an instant next-page paint, and PUBLISHES on of:bench:updated.
     - of-cart-drawer.js renders the lanes from that published state and
       never recomputes them, so the header gauge and the drawer ladder
       cannot disagree.

   The count pill is NOT this file's business. Empire's cartcount:update
   listener owns data-header-cart-count and is the single writer. This file
   only listens to that event, to know when the cart moved.

   Every failure path leaves a plain, correct cart icon and a rail sitting
   in its idle invitation, which is a true statement about an unknown
   cart. */
(() => {
  'use strict';

  const Core = window.OFBenchCore;
  if (!Core) return;

  const root = document.querySelector('[data-of-wbc]');
  const rail = document.querySelector('[data-of-bench]');
  /* Either surface alone is worth running for. The icon and the rail are
     rendered by different snippets in different parts of the header, and
     a missing one must not silently kill the other. */
  if (!root && !rail) return;
  const icon = root && root.querySelector('[data-of-wbc-icon]');
  const gauge = root && root.querySelector('[data-of-wbc-gauge]');
  const srEl = root && root.querySelector('[data-of-wbc-sr]');
  const hasIcon = !!(icon && gauge);

  const STATE_KEY = 'of-bench-state';

  const once = (name, params) => {
    try {
      if (window.OFTrack && window.OFTrack.once) window.OFTrack.once(name, name, params);
      else if (window.OFTrack && window.OFTrack.event) window.OFTrack.event(name, params);
    } catch (e) {}
  };

  const rules = (window.OF_BADGE_RULES || []).filter((r) => r && r.tiers && r.tiers.length);

  /* ---------- product tags, cached in sessionStorage ----------
     Fallback path only since 2026-09-17: resolveTags below reads the tags
     the cart lines already carry in the DOM and only lands here for a
     handle with no rendered row. */

  const tagCache = {};
  const productTags = async (handle) => {
    if (handle in tagCache) return tagCache[handle];
    try {
      /* Own cache key, not the meganav's tags-only one: badge rules match
         on vendor and product type too (the normalizeProductTags contract
         in of-nudges-core.js), and a rule keyed on "foredom" must light a
         lane here just as it earns a pill in the cart. */
      const key = 'of-bench-tags:' + handle;
      const hit = window.sessionStorage && sessionStorage.getItem(key);
      if (hit !== null) return (tagCache[handle] = JSON.parse(hit));
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) return (tagCache[handle] = null);
      const pj = await res.json();
      const tags = (pj.tags || []).map((t) => String(t).trim().toLowerCase());
      if (pj.type) tags.push(String(pj.type).trim().toLowerCase());
      if (pj.vendor) tags.push(String(pj.vendor).trim().toLowerCase());
      try { sessionStorage.setItem(key, JSON.stringify(tags)); } catch (e) {}
      return (tagCache[handle] = tags);
    } catch (e) { return (tagCache[handle] = null); }
  };

  /* Cart line tags are already in the HTML. snippets/cart-item.liquid emits
     data-of-tags per line (joined on '||', downcased), and the cart drawer
     renders those lines on every page except /cart, where the cart page
     renders them itself. Rebuilt on every refresh, because the drawer
     re-renders its lines through the Section Rendering API. */
  const domTagMap = () => {
    const map = {};
    document.querySelectorAll('.of-item-nudges[data-of-handle]').forEach((el) => {
      const handle = el.getAttribute('data-of-handle');
      if (!handle || map[handle]) return;
      map[handle] = (el.getAttribute('data-of-tags') || '')
        .split('||').map((s) => s.trim().toLowerCase()).filter(Boolean);
    });
    return map;
  };

  /* The DOM row plus the /cart.js line item covers the whole badge-rule
     contract: bench-core matches only rule.any and rule.all against this
     flat list, and the two fields beyond tags that normalizeProductTags
     adds, product type and vendor, both ride on the cart line item itself
     (`product_type`, `vendor`). So a rule keyed on "foredom" still lights
     a lane with no /products/<handle>.js fetch at all (perf review F4).
     The fetch stays for a handle with no rendered row. */
  const resolveTags = async (items) => {
    const map = {};
    const dom = domTagMap();
    const misses = [];
    (items || []).forEach((it) => {
      if (!it || !it.handle || map[it.handle]) return;
      const tags = dom[it.handle];
      if (!tags) {
        if (misses.indexOf(it.handle) === -1) misses.push(it.handle);
        return;
      }
      const full = tags.slice();
      if (it.product_type) full.push(String(it.product_type).trim().toLowerCase());
      if (it.vendor) full.push(String(it.vendor).trim().toLowerCase());
      map[it.handle] = full;
    });
    for (const handle of misses) map[handle] = await productTags(handle);
    return map;
  };

  /* ---------- the gauge ---------- */

  /* The enamel fill. Gold outranks everything: one pool at its top tier
     crowns the whole icon. Otherwise the started deals stripe their own
     rule colours across the fill, and the fill's WIDTH is the deepest
     ladder's progress, so colour says which deals and length says how far.
     Same colour language as the old bench tag (Rhett 8/28). */
  const fillBackground = (g) => {
    /* Gold only (Rhett 8/29: "just go gold, no other colors"). The mixed
       heritage fills read as mud at glyph size; colour belongs to the
       LANES, the glyphs speak one material. Top tier is the full old
       gold; merely started is the soft version, so gold-means-best-price
       survives as intensity instead of hue. */
    if (g.gold) return 'linear-gradient(90deg, #96691e, ' + Core.GOLD + ' 58%, #e3bd72)'; // of-keep: bar
    return 'linear-gradient(180deg, color-mix(in srgb, ' + Core.GOLD + ' 40%, #fff), color-mix(in srgb, ' + Core.GOLD + ' 78%, #fff))'; // of-keep: bar
  };

  const paintGauge = (g) => {
    if (!hasIcon) return;
    if (!g || !g.n) { gauge.innerHTML = ''; root.removeAttribute('data-of-wbc-deals'); return; }
    let bar = gauge.querySelector('i');
    if (!bar) { bar = document.createElement('i'); gauge.appendChild(bar); }
    bar.style.width = Math.max(4, g.fillPct).toFixed(1) + '%';
    bar.style.background = fillBackground(g);
    root.setAttribute('data-of-wbc-deals', g.gold ? 'gold' : 'on');
  };

  /* Items land on the benchtop, up to three. Past that the count pill is
     the honest number and more silhouettes only cost legibility. */
  const paintItems = (n) => {
    if (!hasIcon) return;
    icon.setAttribute('data-of-wbc-items', String(Math.max(0, Math.min(3, Number(n) || 0))));
  };

  const paintSr = (n) => {
    if (!srEl) return;
    const count = Number(n) || 0;
    srEl.textContent = count > 0
      ? 'Your bench, ' + count + (count === 1 ? ' item' : ' items')
      : 'Your bench, empty';
  };

  /* The wax seal: struck once, on the transition into gold. */
  const stampSeal = () => {
    if (!hasIcon) return;
    if (icon.querySelector('.of-wbc__seal')) return;
    const seal = document.createElement('span');
    seal.className = 'of-wbc__seal';
    seal.textContent = 'OF';
    icon.appendChild(seal);
    window.setTimeout(() => seal.remove(), 2200);
  };

  /* ---------- the header rail ---------- */

  /* snippets/of-bench-rail.liquid. Three states, one of which is server
     rendered and true with no JS at all. Painted from the same published
     state the gauge reads, never from its own computation. */

  const railParts = rail ? {
    idle: rail.querySelector('[data-of-bench-idle]'),
    slot: rail.querySelector('[data-of-bench-slot]'),
    label: rail.querySelector('[data-of-bench-label]'),
    lanes: rail.querySelector('[data-of-bench-minirail]'),
    panel: rail.querySelector('[data-of-bench-panel]'),
    rows: rail.querySelector('[data-of-bench-rows]')
  } : null;
  const hasRail = !!(rail && railParts.slot && railParts.lanes);

  let railWasLive = false;
  let railLastTotal = -1;
  let glintTimer = null;

  /* Rule colours come from merchant-edited metaobject fields and are about
     to be interpolated into a style attribute. The validation moved into
     of-bench-core.js so the drawer's lane template, which interpolates the
     same field into the same attribute, runs the same check. */
  const safeColor = Core.safeColor;

  const railState = (name) => {
    if (!hasRail) return;
    rail.setAttribute('data-of-bench-state', name);
    const live = name === 'live' || name === 'loading';
    if (railParts.idle) railParts.idle.hidden = live;
    railParts.slot.hidden = !live;
  };

  /* The tag IS the gauge, in miniature: the fill rises to the deepest
     ladder's progress, started rules stripe their own colours across it,
     and gold crowns a struck top tier. Same colour language as the icon
     under the cart, because they are reading the same state. */
  const paintTag = (g) => {
    if (!hasRail || !g) return;
    const grad = rail.querySelector('#of-bench-fillg');
    if (grad) {
      while (grad.firstChild) grad.removeChild(grad.firstChild);
      const mk = (off, color, op) => {
        const st = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
        st.setAttribute('offset', off);
        st.setAttribute('stop-color', color);
        if (op !== undefined) st.setAttribute('stop-opacity', op);
        grad.appendChild(st);
      };
      if (g.gold) {
        grad.setAttribute('x1', '0'); grad.setAttribute('y1', '1');
        grad.setAttribute('x2', '0'); grad.setAttribute('y2', '0');
        mk('0', '#96691e'); mk('0.55', Core.GOLD); mk('1', '#e3bd72'); // of-keep: bar
      } else {
        /* Gold only (Rhett 8/29): the multi-colour fills read as mud at
           25px. The tag is always old gold when live; the fill level
           still reads as saturation, and a struck top tier goes to the
           rich gradient above. */
        const lvl = Math.max(0.12, Math.min(1, (g.fillPct || 0) / 100));
        grad.setAttribute('x1', '0'); grad.setAttribute('y1', '1');
        grad.setAttribute('x2', '0'); grad.setAttribute('y2', '0');
        const op = (0.35 + 0.55 * lvl).toFixed(2);
        mk('0', '#d4ac60', op); mk('1', Core.GOLD, op); // of-keep: bar
      }
    }
    /* the outline follows the gold too; the rule colours stay in the lanes */
    const strokeColor = g.gold ? '#96691e' : '#a97e2f'; // of-keep: line art
    rail.querySelectorAll('.of-bench__slot .of-bench__tag path:not(.of-bench__tagfill)')
      .forEach((el) => { el.style.stroke = strokeColor; });
  };

  const clearTag = () => {
    if (!hasRail) return;
    const grad = rail.querySelector('#of-bench-fillg');
    if (grad) while (grad.firstChild) grad.removeChild(grad.firstChild);
    rail.querySelectorAll('.of-bench__slot .of-bench__tag path')
      .forEach((el) => el.style.removeProperty('stroke'));
  };

  /* layout/theme.liquid stamps TWO pre-paint classes off the session cache:
     of-bench-live0 forces the rail's live slot into the lane, of-wbc-live
     lights the gauge track under the cart icon. An idle bench has to take
     BOTH back off the root, or the next paint shows a live slot with
     nothing in it, or an empty gauge track under the bench. */
  const dropPrePaint = () => {
    const cl = document.documentElement.classList;
    cl.remove('of-bench-live0');
    cl.remove('of-wbc-live');
  };

  /* Every path that gives up on the ladder writes the cache too. A bail
     that leaves a stale live:true in sessionStorage makes the next page
     force the live slot pre-paint and then correct itself when the refresh
     bails again: one header flicker per page, forever. */
  const cacheIdle = (count) => {
    dropPrePaint();
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify({
        live: false, lanes: [], gauge: null, gold: false, label: '',
        cartCount: Number(count) || 0, n: 0
      }));
    } catch (e) {}
  };

  const railIdle = () => {
    dropPrePaint();
    if (!hasRail) return;
    clearTag();
    railState('idle');
    panelClose();
    railWasLive = false;
    railLastTotal = -1;
    if (glintTimer) { window.clearInterval(glintTimer); glintTimer = null; }
  };

  /* Items in the cart, no cache, pools unresolved. The label shimmers
     rather than showing a count we have not earned yet. */
  const railLoading = () => {
    if (!hasRail) return;
    if (railParts.label) {
      railParts.label.textContent = 'Your deals';
      railParts.label.classList.add('of-bench--shimmer');
    }
    railParts.lanes.className = 'of-bench__minirail';
    railParts.lanes.innerHTML = '<i></i>';
    railState('loading');
  };

  /* ---------- the rail's own pop-down panel ---------- */

  /* The live slot opens THIS, not the cart drawer: the drawer belongs to
     the cart button on the far side of Login, and a click here opening a
     slide-in over there read as broken (Rhett 8/29). The rows come from
     the drawer's shared lane template, so the two surfaces cannot drift. */
  const panelRows = () => {
    if (!railParts.rows) return;
    const tpl = window.__OFLaneHtml;
    const lanes = (window.__OFBenchState && window.__OFBenchState.lanes) || [];
    railParts.rows.innerHTML = (tpl && lanes.length) ? lanes.map(tpl).join('') : '';
  };

  const panelOpen = () => {
    if (!railParts.panel) return;
    panelRows();
    railParts.panel.hidden = false;
    railParts.slot.setAttribute('aria-expanded', 'true');
    try { if (window.OFTrack && window.OFTrack.event) window.OFTrack.event('of_bench_open', { surface: 'rail' }); } catch (e) {}
  };

  const panelClose = () => {
    if (!railParts.panel || railParts.panel.hidden) return;
    railParts.panel.hidden = true;
    railParts.slot.setAttribute('aria-expanded', 'false');
  };

  if (hasRail && railParts.panel) {
    railParts.slot.addEventListener('click', () => {
      if (railParts.panel.hidden) panelOpen(); else panelClose();
    });
    document.addEventListener('click', (e) => {
      if (!railParts.panel.hidden && !rail.contains(e.target)) panelClose();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !railParts.panel.hidden) { panelClose(); railParts.slot.focus(); }
    });
  }

  const paintRail = (state, silent) => {
    if (!hasRail) return;
    const shown = (state && state.gauge && state.gauge.lanes) || [];
    if (!state || !state.live || !shown.length) { railIdle(); return; }

    paintTag(state.gauge);
    if (railParts.label) {
      railParts.label.textContent = state.label || Core.benchLabel({ lanes: shown });
      railParts.label.classList.remove('of-bench--shimmer');
    }

    /* One lane per started deal, each filling to its own progress in its
       own colour. A finished ladder keeps its lane and turns gold: the
       won state is the one worth showing. */
    const fg = (l) => (l.complete ? Core.GOLD : safeColor(l.color));
    const bar = (l) => '<i class="' + (l.near && !l.complete ? 'is-near' : '') + '"'
      + ' style="width: ' + Math.max(0, Math.min(100, Number(l.pct) || 0)).toFixed(1) + '%;'
      + ' --of-bench-fg: ' + fg(l) + ';"></i>';
    railParts.lanes.className = shown.length > 1
      ? 'of-bench__minirail of-bench__minirail--multi'
      : 'of-bench__minirail';
    railParts.lanes.innerHTML = shown.map(bar).join('');

    railState('live');
    if (railParts.panel && !railParts.panel.hidden) panelRows();

    /* Ceremony. Reduced motion is handled in CSS, which gates every one
       of these animations. `silent` is the cached first paint: the slot
       has always been live from the customer's point of view, so it must
       not flip in as if it just happened. */
    const total = shown.reduce((a, l) => a + (Number(l.qty) || 0), 0);
    const firstLive = !railWasLive;
    if (!silent) {
      if (firstLive) {
        railParts.slot.classList.add('of-bench--flip');
        window.setTimeout(() => railParts.slot.classList.remove('of-bench--flip'), 800);
      } else if (railLastTotal !== -1 && total !== railLastTotal) {
        railParts.slot.classList.remove('of-bench--tapped');
        void railParts.slot.offsetWidth;
        railParts.slot.classList.add('of-bench--tapped');
        const glyph = railParts.slot.querySelector('.of-bench__glyphwrap');
        if (glyph) {
          glyph.classList.remove('of-bench--shine');
          void glyph.offsetWidth;
          glyph.classList.add('of-bench--shine');
          window.setTimeout(() => glyph.classList.remove('of-bench--shine'), 800);
        }
      }
    }
    railWasLive = true;
    railLastTotal = total;

    /* the count badge is gone (Rhett 8/29: a second red circle reads as a
       cart count); the deal count lives in the label text alone, and the
       periodic glint retired with the badge */
  };

  /* ---------- instant first paint from the session cache ---------- */

  const liquidCount = Number(
    (root && root.getAttribute('data-of-wbc-cartcount'))
    || (rail && rail.getAttribute('data-of-bench-cartcount'))
    || '0'
  );
  let published = null;
  let inFlight = false;
  let wasLive = false;
  paintItems(liquidCount);
  paintSr(liquidCount);
  try {
    const cached = JSON.parse(sessionStorage.getItem(STATE_KEY));
    if (cached && cached.live && cached.cartCount === liquidCount && cached.gauge) {
      paintGauge(cached.gauge);
      paintRail(cached, true);
      published = cached;
      window.__OFBenchState = cached;
      /* The customer has been looking at a live ladder since last page;
         this paint is a continuation, not a fresh view, so the refresh
         below must not fire of_bench_view again. */
      wasLive = true;
    } else if (liquidCount > 0) {
      /* a cart with items and no usable cache: shimmer, do not guess */
      railLoading();
    } else {
      railIdle();
    }
  } catch (e) {}

  /* ---------- refresh ---------- */

  /* Every repaint of the rail goes through here, so the rail cannot drift
     from the gauge or from the drawer: one computation, one publish, three
     surfaces. */
  const publish = (state) => {
    published = state;
    window.__OFBenchState = state;
    paintRail(state);
    try {
      document.dispatchEvent(new CustomEvent('of:bench:updated', { detail: state }));
    } catch (e) {}
  };

  const refresh = async () => {
    if (inFlight) return;
    inFlight = true;
    try {
      if (!rules.length) {
        cacheIdle(0);
        publish({ live: false, lanes: [], gauge: null, cartCount: 0, n: 0 });
        return;
      }
      /* The shared fetcher first: it dedupes concurrent cart reads across
         the nudge scripts and consumes the inline __OF_CART_CACHE, so a
         pageview costs zero extra /cart.js requests. That claim was false
         until 2026-09-17: of-nudges-core.js loaded BELOW this file, so the
         lookup found nothing and every pageview took the fallback (perf
         review F1). Its tag now loads above this one. The fallbacks below
         run only if the core is missing: the inline cache the drawer emits
         (sections/of-cart-drawer.liquid), then the network. The inline
         cache is read and not deleted here, the core owns that delete. */
      let cart;
      try {
        const core = window.__OFNudgesCore;
        if (core && core.getCartShared) cart = await core.getCartShared();
        else if (window.__OF_CART_CACHE) cart = window.__OF_CART_CACHE;
        else cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      } catch (e) { cart = null; }
      if (!cart || !cart.items) {
        /* A failed read must not strand the rail in its loading shimmer.
           If a cached live paint is already up, keep it (stale beats
           blank); otherwise settle into the idle invitation. */
        cacheIdle(liquidCount);
        if (!published) railIdle();
        return;
      }

      paintItems(cart.item_count);
      paintSr(cart.item_count);

      /* A very large cart is not worth n product fetches on every page.
         The rail drops to its invitation rather than holding a shimmer
         nothing is ever going to resolve. */
      if (cart.items.length > 60) { cacheIdle(cart.item_count); railIdle(); return; }

      const tagsByHandle = await resolveTags(cart.items);
      const result = Core.computeLanes(cart.items, tagsByHandle, rules);
      const g = Core.gaugeState(result);

      const prev = published || {};
      const state = {
        live: result.started,
        lanes: result.lanes,
        gauge: result.started ? g : null,
        gold: result.anyGold,
        label: Core.benchLabel(result),
        cartCount: cart.item_count,
        n: result.lanes.length
      };

      paintGauge(state.gauge);

      /* Ceremony: the seal is struck on the transition into gold, not on
         every paint of a gold cart. Reduced motion is handled in CSS. */
      if (state.gold && !prev.gold && wasLive) stampSeal();

      if (result.started && !wasLive) {
        wasLive = true;
        once('of_bench_view', { rules: result.lanes.length });
      }
      if (!result.started) wasLive = false;

      publish(state);
      try { sessionStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch (e) {}
    } finally {
      inFlight = false;
    }
  };

  /* ---------- signals ---------- */

  /* cartcount:update is the theme's single cart-count event and is now
     dispatched by Empire, by of-instant-search.js, by of-kit-upgrade.js,
     by the WebMCP bridge and by the drawer. Painting the benchtop from its
     detail makes the items land the instant an add resolves, before the
     ladder refresh comes back. */
  window.addEventListener('cartcount:update', (ev) => {
    const n = ev && ev.detail ? ev.detail.item_count : null;
    if (n != null) { paintItems(n); paintSr(n); }
    refresh();
  });

  document.addEventListener('of:cart:updated', () => refresh());

  /* Belt and braces: the count pill is written as an ATTRIBUTE and CSS
     paints it with attr(), so any writer that does not dispatch the event
     still moves the DOM here. Observe ONLY the count attribute on the
     count element itself and diff its value: this widget paints its own
     attributes inside .site-header-cart, and a subtree observer there
     turned every repaint into another refresh, an infinite /cart.js loop
     on every page (code review 8/31). */
  const countEl = document.querySelector('[data-header-cart-count]');
  if (countEl && window.MutationObserver) {
    let lastCount = countEl.getAttribute('data-header-cart-count');
    let t = null;
    new MutationObserver(() => {
      const now = countEl.getAttribute('data-header-cart-count');
      if (now === lastCount) return;
      lastCount = now;
      if (t) window.clearTimeout(t);
      t = window.setTimeout(refresh, 500);
    }).observe(countEl, { attributes: true, attributeFilter: ['data-header-cart-count'] });
  }

  refresh();
})();
